/* eslint-disable react-refresh/only-export-components -- Link and useLocaleNavigate are co-exported by design; splitting the file would break all import paths */
import { forwardRef } from 'react';
import { Link as RouterLink, useLocation, useNavigate, type LinkProps, type NavigateOptions, type To } from 'react-router-dom';
import { pathWithLang, langFromPath, DEFAULT_LANG } from '../utils/languages';

/**
 * A <Link> that keeps the visitor in their language.
 *
 * Every internal link is written as the bare English path — `/about`, not
 * `/es/about` — and this adds the prefix for whatever language the current URL
 * is in. Without it, a Spanish visitor clicking "About" would land on the
 * English page, and the entire /es/ tree would be a dead end reachable only
 * from search results.
 *
 * Exported as `Link` so call sites read unchanged; only the import path moves.
 * Absolute URLs, anchors and mailto: are passed through untouched.
 */
const Link = forwardRef<HTMLAnchorElement, LinkProps>(function LocaleLink({ to, ...rest }, ref) {
  const { pathname } = useLocation();
  const lang = langFromPath(pathname) ?? DEFAULT_LANG;

  let localised: To = to;
  if (typeof to === 'string') {
    // Anything that is not a site-relative path is left alone.
    localised = to.startsWith('/') && !to.startsWith('//') ? pathWithLang(to, lang) : to;
  } else if (to && typeof to === 'object' && typeof to.pathname === 'string' && to.pathname.startsWith('/')) {
    localised = { ...to, pathname: pathWithLang(to.pathname, lang) };
  }

  return <RouterLink ref={ref} to={localised} {...rest} />;
});

export default Link;
export { Link };

/**
 * useNavigate, with the same prefixing applied to string destinations.
 *
 * Options (`state`, `replace`) pass through unchanged, so template loading —
 * which navigates to `/` carrying the chosen wheel in state — keeps working
 * from any language.
 */
export function useLocaleNavigate() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const lang = langFromPath(pathname) ?? DEFAULT_LANG;

  return (to: To | number, options?: NavigateOptions) => {
    if (typeof to === 'number') return navigate(to);
    if (typeof to === 'string' && to.startsWith('/') && !to.startsWith('//')) {
      return navigate(pathWithLang(to, lang), options);
    }
    if (to && typeof to === 'object' && typeof to.pathname === 'string' && to.pathname.startsWith('/')) {
      return navigate({ ...to, pathname: pathWithLang(to.pathname, lang) }, options);
    }
    return navigate(to, options);
  };
}
